import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  inject,
  input,
} from '@angular/core';

@Directive({
  selector: '[poziScrollFade]',
})
export class ScrollFadeDirective implements AfterViewInit, OnDestroy {
  private elRef = inject(ElementRef);

  /** When true, the scroll fade is disabled and all fade shadows are hidden. */
  scrollFadeDisabled = input<boolean>(false);

  protected el: HTMLElement = this.elRef.nativeElement;
  protected resizeObserver: ResizeObserver | undefined;

  @HostListener('scroll')
  onScroll() {
    this.setScrollFade();
  }

  // Was wrapped in `NgZone.runOutsideAngular` so a resize could not trigger change
  // detection. `setScrollFade` writes CSS custom properties directly and reads no
  // signal, so under zoneless nothing it does can schedule a tick — the wrapper had
  // nothing left to do and went with the `NgZone` injection.
  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.setScrollFade());
    this.resizeObserver?.observe(this.el);
  }

  protected setScrollFade() {
    if (this.scrollFadeDisabled()) {
      this.el.style.setProperty('--vertical-bottom-shadow-opacity', '0');
      this.el.style.setProperty('--vertical-top-shadow-opacity', '0');
      this.el.style.setProperty('--horizontal-right-shadow-opacity', '0');
      this.el.style.setProperty('--horizontal-left-shadow-opacity', '0');
      return;
    }

    const verticalScrollable = this.el.clientHeight < this.el.scrollHeight;
    const horizontalScrollable = this.el.clientWidth < this.el.scrollWidth;
    const showVerticalBottomShadow =
      verticalScrollable &&
      this.el.scrollTop + this.el.clientHeight < this.el.scrollHeight;
    const showVerticalTopShadow = verticalScrollable && this.el.scrollTop > 0;
    const showHorizontalRightShadow =
      horizontalScrollable &&
      this.el.scrollLeft + this.el.clientWidth < this.el.scrollWidth;
    const showHorizontalLeftShadow =
      horizontalScrollable && this.el.scrollLeft > 0;

    this.el.style.setProperty(
      '--vertical-bottom-shadow-opacity',
      showVerticalBottomShadow ? '1' : '0'
    );
    this.el.style.setProperty(
      '--vertical-top-shadow-opacity',
      showVerticalTopShadow ? '1' : '0'
    );
    this.el.style.setProperty(
      '--horizontal-right-shadow-opacity',
      showHorizontalRightShadow ? '1' : '0'
    );
    this.el.style.setProperty(
      '--horizontal-left-shadow-opacity',
      showHorizontalLeftShadow ? '1' : '0'
    );
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}
